// DiaryPhotoGallery — full-size image grid for diary detail (W32 polish).
//
// Mirrors AnniversaryPhotoGallery placement.  Each photo is a 2/3-col
// grid square that opens the shared PhotoLightbox on tap.  44×44 touch
// target per Apple HIG.
//
// Photos come from `DiaryImageOut.media_id`; the lightbox reads the binary
// via `/api/v1/media/{media_id}/file` (per `mediaFileUrl()` helper).

import { useState } from "react";
import { Camera } from "lucide-react";

import { Card } from "@/components/ui/card";
import { PhotoLightbox, type PhotoLightboxPhoto } from "@/components/albums/PhotoLightbox";
import { mediaFileUrl } from "@/lib/api/media";
import type { DiaryImageOut } from "@/lib/api/diary";

export interface DiaryPhotoGalleryProps {
  images: readonly DiaryImageOut[];
}

export function DiaryPhotoGallery({ images }: DiaryPhotoGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  const photos: PhotoLightboxPhoto[] = images.map((img, idx) => ({
    id: img.id,
    media_id: img.media_id,
    label: `圖 ${idx + 1}`,
  }));

  return (
    <section
      data-component="DiaryPhotoGallery"
      aria-label="日記圖片"
      data-testid="diary-photo-gallery"
      className="flex flex-col gap-s-3"
    >
      <header className="flex items-center gap-2 px-1">
        <span
          aria-hidden
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper-tint1 text-ink-2"
        >
          <Camera size={18} strokeWidth={1.5} />
        </span>
        <p className="text-micro uppercase tracking-wider text-ink-3 font-semibold">
          照片 · {images.length} 張
        </p>
      </header>
      <div
        className="grid grid-cols-2 md:grid-cols-3 gap-2"
        data-testid="diary-photo-gallery-grid"
      >
        {images.map((img, idx) => (
          <Card
            key={img.id}
            className="overflow-hidden p-0 cursor-pointer press hover:shadow-modal transition-shadow"
          >
            <button
              type="button"
              data-testid={`diary-photo-${img.id}`}
              aria-label={`打開第 ${idx + 1} 張圖`}
              onClick={() => setLightboxIndex(idx)}
              className="block w-full aspect-square focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
            >
              <img
                src={mediaFileUrl(img.media_id)}
                alt={`日記圖片 ${idx + 1}`}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          </Card>
        ))}
      </div>
      <PhotoLightbox
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        photos={photos}
        startIndex={lightboxIndex ?? 0}
        title="日記照片"
        testId="diary-photo-lightbox"
      />
    </section>
  );
}
